{
  "dashboard": {
    "title": "Dashboard",
    "subtitle": "Overview of your garden status"
  },
  "sensors": {
    "title": "Sensor Management",
    "subtitle": "Overview and calibration of all system sensors",
    "sections": {
      "chemistry": "Solution chemistry",
      "temperature": "Temperature",
      "levels": "Levels",
      "flow": "Flow",
      "other": "Other"
    },
    "typeLabels": {
      "ph": "Acidity (pH)",
      "ec": "Nutrient strength (EC)",
      "temperature": "Temperature",
      "level": "Level",
      "flow": "Flow"
    },
    "status": {
      "fault": "Fault",
      "offline": "Not connected",
      "ok": "Working"
    },
    "offlineHint": "What to try: 1) Check wiring 2) Verify WiFi 3) Restart the ESP32",
    "calibration": {
      "good": "OK",
      "needed": "Needed",
      "error": "Error"
    },
    "card": {
      "currentValue": "Current value",
      "calibration": "Calibration",
      "lastCalibration": "Last: {{date}}"
    },
    "actions": {
      "settings": "Settings",
      "calibrate": "Calibrate",
      "showTrend": "Show trend",
      "refresh": "Refresh"
    },
    "stats": {
      "working": "Working",
      "needsCalibration": "Needs calibration",
      "faults": "Faults"
    },
    "loadError": "Error loading sensors",
    "empty": {
      "title": "No sensors",
      "description": "Add sensors to the system to manage and calibrate them. Sensors are detected automatically once a device connects.",
      "action": "Refresh list"
    }
  },
  "controls": {
    "title": "System Controls",
    "subtitle": "Manual control of all devices ({{count}} devices)"
  },
  "cycle": {
    "title": "Aeroponic pump cycle",
    "onTime": "On time (ON)",
    "onHint": "How long the pump runs each cycle.",
    "offTime": "Pause (OFF)",
    "offHint": "Delay between runs. Cycle = ON + pause."
  },
  "nightMode": {
    "title": "Pump night mode",
    "description": "During the night window the pump runs at reduced power for quieter, gentler operation.",
    "on": "Enabled",
    "off": "Disabled",
    "start": "Start",
    "end": "End",
    "power": "Pump power",
    "powerHint": "Recommended 25–40%.",
    "kickstart": "Kickstart",
    "kickstartHint": "Brief power burst to start the pump. Recommended 50% (quieter than 100%).",
    "duration": "Run duration",
    "durationHint": "At lower power let the pump run longer (same volume).",
    "save": "Save",
    "saved": "Night mode saved",
    "saveFailed": "Failed to save night mode",
    "statusTitle": "Pump cycle",
    "lastRun": "Last run",
    "nextRun": "Next run",
    "runDuration": "Run duration",
    "never": "Never",
    "soon": "Any moment"
  },
  "dosing": {
    "title": "Nutrient Dosing",
    "subtitle": "Automatic pH and nutrient strength (EC) regulation",
    "calibration": {
      "title": "Pump Calibration",
      "subtitle": "Measure actual flow rate and save the calibration curve.",
      "back": "Back",
      "next": "Next",
      "cancel": "Cancel",
      "save": "Save Calibration",
      "runNow": "Run",
      "again": "Again",
      "addPoint": "Add Point",
      "pump": "Pump",
      "lastCal": "Last Calibration",
      "never": "Never",
      "preview": "Preview",
      "steps": {
        "pick": "Pump",
        "prime": "Prime Tubing",
        "measure": "Measure",
        "review": "Review"
      },
      "pick": {
        "title": "Select a pump to calibrate",
        "subtitle": "Shows connected dosing pumps on the hub.",
        "calibrated": "Calibrated",
        "cleanReminder": "Flush with clean water before calibrating (1 min)."
      },
      "prime": {
        "title": "Prime tubing and prepare container",
        "subtitle": "Ensures accurate results without air bubbles.",
        "checklist": "Checklist",
        "ck1": {
          "t": "Tubing is submerged in solution",
          "h": "Inlet must be below the surface."
        },
        "ck2": {
          "t": "Outlet is over the measuring container",
          "h": "Prepare a graduated cylinder or measuring cup."
        },
        "ck3": {
          "t": "Prime the pump",
          "h": "Click the button below."
        },
        "ck4": {
          "t": "Tubing is full, no air bubbles",
          "h": "Visually check transparent tubing."
        },
        "primeBtn": "Prime (10 s)",
        "primed": "Done. Tubing is primed.",
        "priming": "Priming…",
        "densityToggle": "Solution density",
        "densityHint": "g/ml · default 1.00 (water)",
        "tickAll": "Check all items to continue",
        "tipTitle": "Tip",
        "tip": "After priming, ensure liquid drips evenly from the outlet."
      },
      "measure": {
        "title": "Measure actual flow rate",
        "subtitle": "Run the pump and enter the volume from the measuring container.",
        "pwm": "Power (PWM)",
        "runFor": "Run duration",
        "runHint": "Longer duration gives more accurate results.",
        "pwmHint": "Measure at 25/50/75/100% for a full calibration curve.",
        "live": "Running",
        "done": "Done",
        "ready": "Ready",
        "target": "Estimate",
        "measured": "Measured volume",
        "measuredHint": "Read from the measuring container after the run.",
        "points": "Data points",
        "noPoints": "No points yet. Run a test and add a point.",
        "pwmCovered": "PWM covered",
        "tryNext": "Suggested PWM:",
        "avgRate": "Average flow rate",
        "curve": "Calibration curve"
      },
      "review": {
        "title": "Done. Review and save.",
        "subtitle": "Comparison with previous calibration.",
        "old": "Previous",
        "new": "New",
        "change": "Change",
        "vsOld": "vs. previous calibration",
        "curveType": "Curve type",
        "pwmCurve": "PWM curve",
        "linear": "Linear",
        "multiPoint": "Multi-point",
        "points": "points",
        "consistency": "Consistency",
        "excellent": "Excellent",
        "good": "Good",
        "poor": "Poor",
        "auditTrail": "Journal entry",
        "auditOn": "Calibration will be logged to journal.",
        "notes": "Note",
        "notesPh": "Optional note about this calibration…"
      }
    }
  },
  "automation": {
    "title": "Automation",
    "subtitle": "Set up how the system responds to environmental changes",
    "rulesSubtitle": "Automatic responses to sensor values and device states",
    "newRule": "New rule",
    "templatesTitle": "Quick-start templates",
    "views": {
      "rules": "Rules",
      "history": "History",
      "analytics": "Analytics"
    },
    "stats": {
      "activeRules": "Active rules",
      "ofTotal": "of {{total}}",
      "runningNow": "Running now",
      "rulesUnit": "rules",
      "idle": "idle",
      "runs": "Runs",
      "total": "total",
      "analytics": "Analytics",
      "show": "view"
    },
    "filters": {
      "all": "All",
      "enabled": "Active",
      "running": "Running"
    },
    "running": {
      "title": "Running now",
      "phase": "Step {{current}} of {{total}}",
      "inProgress": "In progress…",
      "finishesIn": "finishes in {{time}}"
    },
    "history": {
      "title": "Run history",
      "empty": "No runs yet",
      "emptyDesc": "Once a rule fires, you will see it here.",
      "filterAll": "All",
      "success": "Success",
      "failed": "Failed",
      "running": "Running",
      "prev": "Previous",
      "next": "Next",
      "pageInfo": "Page {{page}} of {{total}}"
    },
    "analytics": {
      "title": "Analytics",
      "empty": "No data",
      "emptyDesc": "No runs for the selected period.",
      "totalRuns": "Total runs",
      "successRate": "Success rate",
      "failedRuns": "Failures",
      "runsPerDay": "Runs per day",
      "topRules": "Most active rules",
      "daysShort": "{{count}}d"
    },
    "detail": {
      "logic": "Logic",
      "runs": "Runs",
      "priority": "Priority",
      "active": "Active",
      "paused": "Paused",
      "state": "State",
      "lastRun": "Last run",
      "severity": "Severity",
      "recentRuns": "Recent runs",
      "noRecentRuns": "No runs yet",
      "edit": "Edit",
      "duplicate": "Duplicate",
      "pause": "Pause",
      "resume": "Resume",
      "parameters": "Parameters",
      "trigger": "Trigger",
      "sensor": "Sensor",
      "condition": "Condition",
      "hysteresis": "Hysteresis",
      "sustained": "Sustained for",
      "action": "Action",
      "actuator": "Actuator",
      "duration": "Duration",
      "message": "Message",
      "cooldown": "Cooldown",
      "blockedModes": "Blocked in modes",
      "steps": "Steps",
      "sequenceSteps": "Sequence ({{count}} steps)",
      "deviceOn": "When device turns on",
      "deviceOff": "When device turns off",
      "category": "Category"
    },
    "duplicate": {
      "copyName": "{{name}} (copy)",
      "success": "Rule duplicated",
      "safetyUnsupported": "Duplicating safety rules is not supported yet"
    },
    "aeroponic": {
      "title": "Aeroponic cycle",
      "subtitle": "Perpetual firmware-driven misting (survives server restart)",
      "enabled": "Active",
      "disabled": "Off",
      "pump": "Pump",
      "solenoid": "Solenoid (valve)",
      "solenoidNone": "None",
      "onTime": "Run time (ON)",
      "offTime": "Pause (OFF)",
      "save": "Save & apply",
      "saved": "Cycle saved and sent to device",
      "runningInfo": "Running: {{on}} ON / {{off}} OFF — firmware-driven",
      "note": "Runs on the device, independent of the server. Replaces the old rule-based misting.",
      "pumps": {
        "suction": "Suction pump",
        "phPlus": "pH+ pump",
        "phMinus": "pH− pump",
        "nutrientA": "Nutrient A pump",
        "nutrientB": "Nutrient B pump"
      },
      "solenoids": {
        "pb0": "Plant branch",
        "pb1": "Bypass",
        "pb2": "Cooling loop"
      },
      "units": {
        "sec": "s",
        "min": "min"
      },
      "power": "Pump power",
      "kickstart": "Kickstart",
      "kickstartHint": "Short burst to start the pump; tune by observation (0 = off)",
      "nightMode": "Night mode",
      "nightStart": "Start",
      "nightEnd": "End",
      "nightPower": "Night power"
    },
    "disinfection": {
      "title": "Disinfection",
      "subtitle": "Daily night ozonation + mixing (firmware-driven)",
      "enabled": "Active",
      "disabled": "Off",
      "startTime": "Start time",
      "duration": "Duration",
      "minutes": "min",
      "actuators": "Devices",
      "ozonizer": "Ozonizer",
      "mixer": "Mixing",
      "note": "Runs once per day at the set time; firmware turns the actuators off after the duration.",
      "save": "Save",
      "saved": "Disinfection saved",
      "runNow": "Run now",
      "runStarted": "Disinfection started"
    }
  },
  "safety": {
    "title": "Safety Guards",
    "subtitle": "Automatic protection of your system and plants."
  },
  "batches": {
    "title": "My Batches",
    "subtitle": "Management and tracking of growing cycles"
  },
  "journal": {
    "title": "Growing Journal",
    "subtitle": "Records and observations of your plantings"
  },
  "recipes": {
    "title": "Growing Recipes",
    "subtitle": "Define growth phases and fertilization for your crops",
    "presetsTitle": "Dosing Tables (Templates)",
    "presetsSubtitle": "Catalog of fertilizer schedules from manufacturers — use as a base for a new recipe",
    "createRecipe": "Create recipe",
    "newTable": "New table",
    "cardTitle": "Growing recipes and manufacturer templates",
    "cardDesc": "This page combines custom recipes, dosing tables, and multi-stage growth phases into a single design workflow.",
    "tabMyRecipes": "My recipes",
    "tabRecipes": "Recipes",
    "tabManufacturerTemplates": "Manufacturer templates",
    "tabTemplates_zero": "{{count}} templates",
    "tabTemplates_one": "{{count}} template",
    "tabTemplates_other": "{{count}} templates",
    "countRecipes_zero": "{{count}} recipes",
    "countRecipes_one": "{{count}} recipe",
    "countRecipes_other": "{{count}} recipes",
    "editorOpen": "Editor open",
    "overviewActive": "Overview active",
    "myRecipesTab": "My recipes ({{count}})",
    "templatesTab": "Manufacturer templates ({{count}})",
    "emptyTitle": "No growing recipes",
    "emptyDesc": "Create your first recipe or use a dosing table as a template.",
    "noDescription": "No description",
    "growthPhases": "Growth phases",
    "days": "days",
    "emptyTemplatesTitle": "No dosing tables",
    "emptyTemplatesDesc": "Create a new fertilizer schedule or import from a manufacturer.",
    "addFirstTable": "Add first table",
    "phases_zero": "{{count}} phases",
    "phases_one": "{{count}} phase",
    "phases_other": "{{count}} phases",
    "ingredients": "ingredients",
    "useAsTemplate": "Use as template",
    "editRecipe": "Edit recipe",
    "newRecipeTitle": "New growing recipe",
    "fieldName": "Name",
    "namePlaceholder": "e.g. Tomato recipe",
    "fieldPlantType": "Plant type",
    "fieldDescription": "Description",
    "addPhase": "Add phase",
    "phaseN": "Phase {{num}}",
    "fieldPhaseName": "Phase Name",
    "fieldDuration": "Duration (days)",
    "fieldLight": "Light (hrs)",
    "hintLight": "Typically 12–18 hours",
    "fieldMinPh": "Min pH",
    "hintPh": "Most vegetables: 5.5–6.5",
    "fieldMaxPh": "Max pH",
    "fieldMinEc": "Min EC",
    "hintEcLeafy": "Leafy greens: 0.8–1.5 mS/cm",
    "fieldMaxEc": "Max EC",
    "hintEcFruit": "Fruiting vegetables: 2.0–3.0 mS/cm",
    "fieldMinTemp": "Min Temp",
    "hintTemp": "Most crops: 18–28 °C",
    "fieldMaxTemp": "Max Temp",
    "nutrientsTitle": "Fertilizers / Additives (per 10l water)",
    "addIngredient": "Add ingredient",
    "selectFertilizer": "Select fertilizer...",
    "placeholderAmount": "Amount",
    "placeholderUnit": "Unit",
    "noNutrients": "No fertilizers for this phase.",
    "pickFromTemplates": "Pick from templates",
    "cancel": "Cancel",
    "saveChanges": "Save changes",
    "confirmDeleteTitle": "Delete recipe?",
    "confirmDeleteText": "This action is irreversible. The recipe will be permanently deleted.",
    "delete": "Delete",
    "confirmDeleteTableTitle": "Delete dosing table?",
    "confirmDeleteTableText": "Are you sure you want to delete this dosing table? This action is irreversible.",
    "toastLoadError": "Failed to load recipes.",
    "toastLoadTablesError": "Failed to load dosing tables.",
    "toastTableDeleted": "Table deleted",
    "toastTableDeleteError": "Failed to delete table. Please try again.",
    "toastTableUpdated": "Table updated",
    "toastTableCreated": "Table created",
    "toastTableSaveError": "Failed to save table. Please try again.",
    "toastTemplateLoaded": "Template \"{{name}}\" loaded into new recipe",
    "toastNameRequired": "Please enter a recipe name",
    "toastRecipeUpdated": "Recipe updated",
    "toastRecipeCreated": "Recipe created",
    "toastRecipeSaveError": "Failed to save recipe. Please try again.",
    "toastRecipeDeleted": "Recipe deleted",
    "toastRecipeDeleteError": "Failed to delete recipe. Please try again.",
    "plantTypes": {
      "lettuce": "Leafy vegetables",
      "tomato": "Tomato vegetables",
      "pepper": "Peppers & Chilli",
      "herb": "Herbs (Basil etc.)",
      "cannabis": "Medicinal Cannabis",
      "flower": "Ornamental flowers",
      "other": "Other"
    },
    "defaultPhaseName": "New Phase",
    "defaultFirstPhaseName": "Seedling",
    "templateSuffix": "(Template)"
  },
  "nutrientPresets": {
    "title": "Dosing Tables",
    "subtitle": "Catalog of fertilizer schedules for your plantings."
  },
  "reservoirs": {
    "title": "Reservoirs",
    "subtitle": "Reservoir management for device {{device}}",
    "form": {
      "nameRequired": "Reservoir name is required",
      "nameLabel": "Reservoir name *",
      "capacityLabel": "Volume (liters)",
      "cancel": "Cancel",
      "save": "Save",
      "create": "Create",
      "addTitle": "New reservoir",
      "editTitle": "Edit reservoir"
    },
    "card": {
      "default": "Default",
      "hideSensors": "Hide sensors",
      "showSensors": "Show sensors",
      "editTitle": "Edit reservoir",
      "deleteTitle": "Delete reservoir",
      "dosingTitle": "Dosing",
      "assignedSensors": "Assigned sensors",
      "removeSensorTitle": "Remove sensor from reservoir",
      "addSensorTitle": "Assign sensor to reservoir",
      "noSensors": "No sensors are assigned to this reservoir.",
      "availableSensors": "Available sensors (unassigned)",
      "allSensorsAssigned": "All sensors are already assigned.",
      "sensorAssigned": "Sensor assigned to reservoir",
      "sensorAssignFailed": "Failed to assign sensor",
      "sensorUnassigned": "Sensor removed from reservoir",
      "sensorUnassignFailed": "Failed to remove sensor"
    },
    "noDevice": {
      "subtitle": "Reservoirs are created per device, so you need to connect or assign a device first.",
      "title": "No device",
      "description": "First assign a device to your account. Reservoirs are created per device."
    },
    "overview": {
      "title": "Reservoir management overview",
      "activeDevice": "Active device: {{device}}",
      "reservoirCount": "{{count}} reservoirs",
      "defaultReservoir": "Default: {{name}}",
      "noDefault": "No default reservoir",
      "assignedSensors": "{{count}} assigned sensors"
    },
    "actions": {
      "add": "Add reservoir"
    },
    "empty": {
      "title": "No reservoirs",
      "description": "Create your first reservoir for your device. Reservoirs are used to monitor level, pH and EC solution.",
      "create": "Create reservoir",
      "dosing": "Go to dosing"
    },
    "infoTip": "Click the arrow next to a reservoir to view and assign sensors. Assign a reservoir to a growing area in its settings (My areas → edit).",
    "delete": {
      "title": "Delete reservoir?",
      "confirmText": "Delete",
      "description": "Are you sure you want to delete reservoir \"{{name}}\"? This action is irreversible and will remove all data associated with this reservoir."
    },
    "toasts": {
      "created": "Reservoir created",
      "duplicateIndex": "A reservoir with this index already exists",
      "createFailed": "Failed to create reservoir",
      "updated": "Reservoir updated",
      "updateFailed": "Failed to update reservoir",
      "deleted": "Reservoir deleted",
      "deleteFailed": "Failed to delete reservoir"
    }
  },
  "tasks": {
    "title": "Tasks",
    "subtitle": "Calendar and task list"
  },
  "cropPlans": {
    "title": "Crop Plans",
    "subtitle": "Plan and track your growing cycles"
  },
  "cropPlanList": {
    "status": {
      "draft": "Draft",
      "active": "Active",
      "completed": "Completed",
      "archived": "Archived"
    },
    "card": {
      "createdBadge": "Created {{date}}",
      "activateTitle": "Activate plan",
      "completeTitle": "Mark as completed",
      "archiveTitle": "Archive",
      "editTitle": "Edit",
      "deleteTitle": "Delete",
      "noDescription": "No description",
      "created": "Created: {{date}}",
      "start": "Start: {{date}}"
    },
    "actions": {
      "fromPast": "From past",
      "templates": "Templates",
      "new": "New plan"
    },
    "overview": {
      "title": "Crop plans overview",
      "total": "{{count}} total",
      "active": "{{count}} active",
      "completed": "{{count}} completed",
      "activeFilter": "Active filter:"
    },
    "filter": {
      "all": "All",
      "draft": "Drafts",
      "active": "Active",
      "completed": "Completed",
      "archived": "Archive"
    },
    "empty": {
      "allTitle": "No plans",
      "filteredTitle": "No plans in filter {{filter}}",
      "allDescription": "Create your first crop plan to organize your growing.",
      "filteredDescription": "No plan is in this state right now. Switch filter or create a new plan.",
      "createAction": "Create plan",
      "showAllAction": "Show all plans",
      "fromPastAction": "From past"
    },
    "delete": {
      "title": "Delete plan?",
      "description": "Are you sure you want to delete this crop plan? This action is irreversible.",
      "confirmText": "Delete",
      "cancelText": "Cancel"
    },
    "pastPlan": {
      "modalTitle": "Create from past plan",
      "description": "Copies all plants and quantities from the selected past plan. This lets you easily repeat last year's planting with a new start date.",
      "selectLabel": "Select completed plan",
      "selectPlaceholder": "-- Select plan --",
      "nameLabel": "New plan name",
      "namePlaceholder": "E.g. Summer bed 2026",
      "startLabel": "Expected start date",
      "startHint": "Date from which the growing steps in this new plan will be counted.",
      "cancel": "Cancel",
      "create": "Create copy",
      "defaultName": "Copy of old plan"
    },
    "toasts": {
      "deleted": "Plan deleted successfully",
      "deleteFailed": "Failed to delete plan",
      "activated": "Plan activated",
      "activateFailed": "Failed to activate plan",
      "completed": "Plan marked as completed",
      "completeFailed": "Failed to complete plan",
      "archived": "Plan archived",
      "archiveFailed": "Failed to archive plan",
      "fillAll": "Please fill in all required fields",
      "copied": "New plan copied successfully!",
      "copyFailed": "Failed to copy plan"
    }
  },
  "settings": {
    "title": "Settings",
    "subtitle": "Configure your hydroponic system and account security"
  },
  "profile": {
    "title": "My Profile",
    "subtitle": "Manage your account and personal settings"
  },
  "notifications": {
    "title": "Notifications and system alerts",
    "subtitle": "Monitor and manage system events and critical states of your hydroponic system."
  },
  "analytics": {
    "title": "Analytics",
    "subtitle": "Detailed analysis and historical data from your hydroponic system"
  },
  "plantopedia": {
    "title": "Plant-O-Pedia",
    "subtitle": "Encyclopedia of plants, pests, and growing practices.",
    "tabs": {
      "plants": "Plants",
      "pests": "Pests",
      "gnn": "GNN Predictions"
    },
    "search": "Search plants...",
    "searchPests": "Search pests...",
    "filterCategory": "Category",
    "filterDifficulty": "Difficulty",
    "resultCount_one": "{{count}} plant found",
    "resultCount_other": "{{count}} plants found",
    "pestResultCount_one": "{{count}} pest found",
    "pestResultCount_other": "{{count}} pests found",
    "noResults": "Nothing found",
    "noResultsHint": "Try adjusting your filters or search term.",
    "beginnerSection": "Recommended for beginners",
    "addSpecies": "Add species",
    "detail": {
      "tabs": {
        "overview": "Overview",
        "growth": "Growth",
        "conditions": "Conditions",
        "health": "Health",
        "varieties": "Varieties"
      },
      "edit": "Edit",
      "back": "Back to list",
      "scientificName": "Scientific name",
      "difficulty": "Difficulty",
      "category": "Category",
      "lifecycle": "Life cycle",
      "sowingPeriod": "Sowing period",
      "harvestPeriod": "Harvest period",
      "height": "Plant height",
      "companions": "Companions",
      "pests": "Pests and diseases",
      "noData": "No data available"
    },
    "form": {
      "newSpecies": "New species",
      "editSpecies": "Edit species",
      "step1": "Basic info",
      "step2": "Growth & Sowing",
      "step3": "Phases",
      "step4": "Pests & Companions",
      "step5": "Context & Summary",
      "name": "Name",
      "namePlaceholder": "E.g. Tomato",
      "nameRequired": "Required field",
      "scientificName": "Scientific name",
      "description": "Description",
      "descriptionPlaceholder": "Species characteristics...",
      "category": "Category",
      "lifecycle": "Life cycle",
      "annual": "Annual",
      "perennial": "Perennial",
      "biennial": "Biennial",
      "difficulty": "Growing difficulty",
      "difficultyLabels": [
        "Trivial",
        "Easy",
        "Medium",
        "Hard",
        "Expert only"
      ],
      "sowingGeneral": "Sowing Period (General)",
      "sowingIndoor": "Indoor Sowing",
      "sowingOutdoor": "Outdoor Sowing",
      "harvestPeriod": "Harvest Period",
      "plantHeight": "Plant height",
      "needsSupport": "Needs support",
      "spacing": "Recommended spacing",
      "spacingPlaceholder": "E.g. 40x50 cm",
      "sowingDepth": "Sowing depth",
      "surfaceSowing": "Surface sow only (do not cover)",
      "growthType": "Growth type",
      "pestsTitle": "Pests and Diseases",
      "addPest": "Add record",
      "noPests": "No pests or diseases recorded yet.",
      "addFirstPest": "Add first record",
      "pestCategoryPests": "Pests (Insects, birds, slugs...)",
      "pestCategoryDiseases": "Diseases (Fungi, bacteria, viruses)",
      "propagateCompanions": "Update companion records too",
      "propagateHint": "If checked, related species will have their companion lists updated to include this species.",
      "contextGreenhouse": "Greenhouse",
      "contextOutdoor": "Outdoor",
      "contextHydroponic": "Hydroponic",
      "contextTitle": "Where to grow (Context)",
      "summary": "Summary",
      "summaryName": "Name",
      "summaryLatin": "Scientific",
      "summaryHeight": "Height",
      "summaryPhases": "Phases",
      "summaryPhaseDefined": "defined",
      "summaryPests": "Pests",
      "summaryPestRecords": "records",
      "prev": "Back",
      "next": "Next",
      "save": "Save",
      "delete": "Delete",
      "aiSuggest": "AI Legacy",
      "aiEnrich": "AI Enrich",
      "aiHelp": "AI help",
      "aiHelpDesc": "auto-fill parameters",
      "aiPests": "AI pests",
      "aiSearching": "Searching…",
      "deletePestTitle": "Delete pest?",
      "deletePestDesc": "Are you sure you want to remove this pest from the list?",
      "deleteSpeciesTitle": "Delete species?",
      "deleteSpeciesDesc": "Are you sure you want to delete species \"{{name}}\"? This action is irreversible.",
      "uploadImage": "Click to upload image"
    },
    "categories": {
      "vegetable": "Vegetable",
      "herb": "Herb",
      "fruit": "Fruit",
      "flower": "Flower"
    }
  },
  "devices": {
    "title": "My Devices",
    "subtitle": "{{count}} devices registered",
    "noDevices": "No devices yet"
  },
  "hardwareOverview": {
    "header": "Hardware Overview",
    "totalSensors": "Total sensors",
    "online": "Active",
    "offlineError": "Inactive / Error",
    "systemStatus": "System status",
    "refreshData": "Refresh data",
    "errorLoading": "Error loading hardware data",
    "lastUpdate": "Last update"
  },
  "login": {
    "subtitle": "Sign in to your hydroponic system",
    "createAccount": "Create a new account",
    "loginTab": "Sign In",
    "registerTab": "Register",
    "email": "Email address",
    "password": "Password",
    "loginButton": "Sign In",
    "registerButton": "Register",
    "forgotPassword": "Forgot your password?",
    "name": "Name",
    "namePlaceholder": "Your name",
    "emailPlaceholder": "you@email.com",
    "passwordPlaceholder": "Minimum 8 characters",
    "confirmPassword": "Confirm password",
    "confirmPasswordPlaceholder": "Repeat password",
    "require2FA": "As an administrator, you must enable two-factor authentication."
  },
  "forgotPassword": {
    "title": "Password Recovery",
    "subtitle": "Enter your email address and we'll send you a recovery link",
    "emailSent": "Email Sent",
    "checkInbox": "Check your email inbox",
    "sentMessage": "If an account exists with this email address, we have sent you password recovery instructions.",
    "noEmail": "Didn't receive the email? Check your spam folder or try again.",
    "sendAgain": "Send Again",
    "sendLink": "Send Recovery Link",
    "backToLogin": "Back to Sign In"
  },
  "resetPassword": {
    "title": "New Password",
    "subtitle": "Enter your new password",
    "changed": "Password Changed",
    "changedSubtitle": "Your password has been successfully changed",
    "changedMessage": "Your password has been successfully changed. You can now sign in with your new password.",
    "goToLogin": "Go to Sign In",
    "invalidLink": "Invalid Link",
    "invalidLinkDesc": "The password recovery link is invalid or has expired. Please request a new one.",
    "requestNew": "Request New Link",
    "newPassword": "New password (min. 8 characters)",
    "confirmPassword": "Confirm password",
    "setNewPassword": "Set New Password",
    "backToLogin": "Back to Sign In"
  },
  "help": {
    "glossaryTitle": "Glossary",
    "glossarySubtitle": "All important terms in one place — for better orientation in hydroponic growing",
    "searchPlaceholder": "Search term...",
    "noResults": "No term found",
    "tryDifferent": "Try a different search term",
    "example": "Example",
    "categoryBasics": "Hydroponics Basics",
    "categorySystems": "System Types",
    "categoryMeasurement": "Measurement & Sensors",
    "categoryOperation": "Operation & Maintenance"
  },
  "schedule": {
    "title": "Task Scheduler",
    "subtitle": "Manage automatic tasks and scheduled actions for your system"
  },
  "admin": {
    "title": "Administration",
    "subtitle": "System management",
    "users": "User Management",
    "usersSubtitle": "Manage user accounts and permissions",
    "devices": "Device Management",
    "devicesSubtitle": "Manage connected devices",
    "firmware": "Firmware Management",
    "firmwareSubtitle": "OTA updates and firmware version management",
    "hardware": "Hardware Metrics",
    "hardwareSubtitle": "Hardware status and system metrics overview",
    "audit": "Audit Log",
    "auditSubtitle": "System events history",
    "knowledgeSources": "Knowledge Sources",
    "knowledgeSourcesSubtitle": "Manage sources for the RAG pipeline and companion planting verification"
  },
  "branches": {
    "title": "Growing Branches",
    "subtitle": "Manage growing branches",
    "listSubtitle": "Manage growing areas, branches and their plantings.",
    "newBranch": "New Branch",
    "editBranch": "Edit Branch",
    "newGrowingBranch": "New Growing Branch",
    "automated": "Automated",
    "manual": "Manual (No device)",
    "automatedBranches": "Automated Branches",
    "manualBranches": "Manual Branches",
    "activePosition": "Active position",
    "disabledSlot": "Disabled (Empty slot)",
    "addBranch": "Add Branch",
    "limitReached": "Branch limit reached",
    "gridLayout": "Grid Layout",
    "columns": "Columns (width)",
    "rows": "Rows (height)",
    "activeCapacity": "Active Capacity",
    "assignedLighting": "Assigned Lighting",
    "assignedValves": "Assigned Valves",
    "branchControls": "Branch Controls",
    "lighting": "Lighting",
    "valves": "Valves",
    "targetConditions": "Target Conditions",
    "fromActiveRecipes": "From active recipes",
    "activePlants": "Active Plants / Plantings",
    "plantingHistory": "Planting History",
    "noHistory": "No history.",
    "rackView": "Rack View",
    "statusHealthy": "Healthy",
    "statusSick": "Sick",
    "statusDead": "Dead",
    "noActiveDevice": "No active device selected.",
    "selectDeviceForAutomated": "Select a device to view automated branches.",
    "hwIndex": "HW Index",
    "outputNum": "Output #{{num}}",
    "outputTaken": "(Taken)",
    "manualNotConnected": "Manual branch is not connected to any device output.",
    "noLightsFound": "No lights found.",
    "noValvesFound": "No valves found.",
    "optionalDescription": "Optional description...",
    "namePlaceholder": "E.g. Left side",
    "saveChanges": "Save changes",
    "deleteBranchTitle": "Delete branch?",
    "deleteBranchDesc": "Are you sure you want to delete this branch? This action is irreversible and will remove all history data associated with this branch.",
    "maxBranchesPerDevice": "Max: {{max}} branches per device",
    "noManualBranches": "No manual branches",
    "manualBranchesDesc": "Manual branches are used for tracking plants without automation.",
    "addManualBranch": "Add manual branch",
    "clickToToggle": "Position #{{slot}} - Click to {{action}}",
    "enable": "enable",
    "disableAction": "disable",
    "clickToShape": "Click on squares to create an irregular shape (skip positions).",
    "noDescription": "No description",
    "temperature": "Temperature",
    "humidity": "Humidity",
    "light": "Light",
    "nothingGrowing": "Nothing is growing on this branch.",
    "createNewPlanting": "Create new planting",
    "plants": "plants",
    "dayNum": "Day {{num}}",
    "editPlanting": "Edit planting",
    "detail": "Detail",
    "editDate": "Edit date",
    "move": "Move",
    "removeFromBranch": "Remove from branch",
    "irrigationStatus": "Irrigation Status",
    "solenoid": "Solenoid",
    "off": "Off",
    "used": "used",
    "inactivePosition": "Inactive position",
    "positionFree": "Position {{slot}} (Free)",
    "collisionAtPosition": "Collision at position #{{slot}}",
    "collisionDesc": "Multiple plants are at this position. Select which one to edit (e.g. transplant):",
    "plantingStartDate": "Planting start date",
    "dateChangeRecalc": "Changing the date will recalculate plant age.",
    "removeBatchTitle": "Remove planting from branch?",
    "removeBatchDesc": "Are you sure you want to remove planting \"{{name}}\" from this branch? The planting will remain active but won't be assigned to any branch.",
    "deletePlantTitle": "Delete plant?",
    "deletePlantDesc": "Are you sure you want to permanently delete this plant?",
    "branchNotFound": "Branch not found."
  },
  "gardens": {
    "title": "Gardens",
    "subtitle": "Manage your growing locations",
    "create": "New Garden",
    "edit": "Edit Garden",
    "newGarden": "New Garden",
    "editGarden": "Edit Garden",
    "filterAll": "All",
    "emptyTitle": "No gardens yet",
    "emptyDescription": "Create your first garden to organize your growing areas.",
    "createGarden": "Create Garden",
    "bedCount_one": "bed",
    "bedCount_other": "beds",
    "plantCount_one": "plant",
    "plantCount_other": "plants",
    "notFound": "Garden not found",
    "notFoundDescription": "This garden does not exist or has been deleted.",
    "backToList": "Back to list",
    "backToGardens": "Back to gardens",
    "deleteConfirmTitle": "Delete garden?",
    "deleteConfirmDescription": "Are you sure you want to delete garden \"{{name}}\"? Growing areas will be unassigned.",
    "unassignConfirmTitle": "Unassign growing area?",
    "unassignConfirmDescription": "Are you sure you want to unassign this growing area from the garden?",
    "unassign": "Unassign",
    "unassignFromGarden": "Unassign from garden",
    "areasInGarden": "Growing areas in this garden",
    "addArea": "Add area",
    "noAssignedAreas": "No assigned growing areas yet",
    "assignExistingArea": "Assign existing area",
    "assignAreasTitle": "Assign areas to garden",
    "allAreasAssigned": "All growing areas are already assigned to a garden.",
    "assignCount": "Assign ({{count}})",
    "slotsCount": "slots",
    "basicInfo": "Basic Information",
    "nameRequired": "Name *",
    "namePlaceholder": "E.g. Apartment Garden",
    "nameValidation": "Name is required",
    "gardenType": "Garden Type",
    "icon": "Icon",
    "descriptionPlaceholder": "Optional garden description...",
    "location": "Location",
    "address": "Address",
    "addressPlaceholder": "E.g. 123 Main Street",
    "timezone": "Timezone"
  },
  "growingAreas": {
    "title": "Growing Areas",
    "subtitle": "Manage growing areas",
    "create": "New Growing Area",
    "edit": "Edit Growing Area"
  },
  "plants": {
    "title": "Plants",
    "subtitle": "Overview of your plants"
  },
  "calendar": {
    "title": "Calendar",
    "subtitle": "Overview of planned activities"
  },
  "analyticsDetail": {
    "timeRange": {
      "24h": "24 hours",
      "7d": "7 days",
      "30d": "30 days"
    },
    "tabs": {
      "overview": "Overview",
      "phEc": "pH & EC",
      "temperature": "Temperature"
    }
  },
  "cropPlanDetail": {
    "status": {
      "draft": "Draft",
      "active": "Active",
      "completed": "Completed",
      "archived": "Archived"
    },
    "fallbacks": {
      "unknownPlant": "Unknown plant",
      "noPlanDescription": "Growing plan",
      "templateNameSuffix": " - Template",
      "newTemplateName": "New template"
    },
    "item": {
      "placed": "Placed {{placed}} of {{total}}",
      "dates": {
        "sow": "Sow:",
        "transplant": "Transplant:",
        "harvest": "Harvest:"
      },
      "placements": {
        "label": "Placements",
        "add": "Add",
        "positions": "{{count}} positions",
        "executed": "Executed",
        "cancelled": "Cancelled",
        "planned": "Planned",
        "empty": "No placements"
      }
    },
    "toasts": {
      "activated": "Plan activated",
      "activateFailed": "Failed to activate plan",
      "completed": "Plan completed",
      "completeFailed": "Failed to complete plan",
      "executed": "Plan successfully executed for batch: {{batchName}}",
      "executeFailed": "Failed to execute plan",
      "itemRemoved": "Item removed",
      "itemRemoveFailed": "Failed to remove item",
      "itemUpdated": "Item updated",
      "itemAdded": "Item added",
      "itemSaveFailed": "Failed to save item",
      "templateNameRequired": "Enter a template name",
      "templateSaved": "Plan successfully saved as a template",
      "templateSaveFailed": "Failed to save template",
      "placementCancelled": "Placement cancelled",
      "placementCancelFailed": "Failed to cancel placement",
      "bulkCancelled": "{{count}} placements cancelled",
      "bulkCancelFailed": "Failed to bulk cancel placements",
      "bulkPlaced": "{{count}} placements created",
      "bulkPlaceFailed": "Failed to bulk place plants",
      "plantPlaced": "Plant placed in slot",
      "placeFailed": "Failed to place plant",
      "placementModeHint": "Now click on slots in the map to place this item"
    },
    "confirm": {
      "executePlan": {
        "title": "Execute plan?",
        "description": "Are you sure you want to execute this plan? A new batch will be created and plants will be assigned to slots."
      },
      "removeItem": {
        "title": "Remove item?",
        "description": "Are you sure you want to remove this item from the plan?"
      },
      "cancelPlacement": {
        "title": "Cancel placement?",
        "description": "Are you sure you want to cancel this slot placement?"
      },
      "bulkCancel": {
        "title": "Cancel {{count}} placements?",
        "description": "Are you sure you want to cancel {{count}} selected placements?"
      },
      "confirmText": "Confirm",
      "cancelText": "Cancel"
    },
    "reality": {
      "noBatchYet": "The plan has no executed batch yet, reality metrics are zero.",
      "exactMatch": "The actual count exactly matches the plan.",
      "overPlan": "Reality is {{delta}} units above the plan.",
      "underPlan": "Plan is missing {{count}} units.",
      "sectionLabel": "Plan vs reality",
      "sectionTitle": "Reality snapshot for this plan",
      "linkedBatches": "Linked batches",
      "vsLabel": "Execution vs plan",
      "actualVsPlanned": "{{actual}} actually created from planned {{planned}} units",
      "metrics": {
        "plan": "Plan",
        "planHelper": "Sum of quantity across all items",
        "founded": "Founded",
        "foundedHelper": "Actual plants in linked batches",
        "germination": "Germination",
        "germinationHelper": "{{rate}}% of actually founded plants",
        "active": "Active",
        "activeHelper": "Currently alive plants",
        "mortality": "Mortality",
        "mortalityHelper": "{{rate}}% of actually founded plants",
        "harvest": "Harvest",
        "harvestHelper": "Plants marked as harvested"
      },
      "warnNoStats": "The plan has executed placements, but no batch statistics are linked yet. New executions already store crop_plan_id and will be reflected here automatically."
    },
    "stats": {
      "created": "Created",
      "start": "Start",
      "items": "Items",
      "successRate": "Plan success rate",
      "planted": "planted"
    },
    "badges": {
      "items": "{{count}} items",
      "batches": "{{count}} batches",
      "linkedArea": "Linked to area",
      "noArea": "No target area"
    },
    "header": {
      "detailTitle": "Plan detail, execution and linked batches",
      "detailSubtitle": "Detail combines planned items, placement workflow, reality metrics and connection to the target area."
    },
    "targetArea": {
      "label": "Target area",
      "description": "Plan is assigned to a specific growing area"
    },
    "actions": {
      "activate": "Activate",
      "executeRun": "Execute batch",
      "complete": "Complete",
      "saveAsTemplate": "Save as template",
      "settings": "Settings",
      "addItem": "Add item"
    },
    "itemsSection": {
      "title": "Items to plant",
      "empty": {
        "title": "No items yet",
        "description": "Add the first plant you want to grow in this plan."
      }
    },
    "visualizer": {
      "title": "Placement visualizer",
      "placementMode": "Placement mode:",
      "plantFallback": "Plant"
    },
    "templateModal": {
      "title": "Save plan as template",
      "description": "The template will remember all items from this plan (species, variety, and quantity), but won't save specific dates and slot placements. Ideal for repeating a successful plot next year.",
      "nameLabel": "Template name",
      "namePlaceholder": "E.g. Spring mix for front plot",
      "descLabel": "Description (optional)",
      "descPlaceholder": "Notes about this template...",
      "cancel": "Cancel",
      "save": "Save template"
    },
    "itemModal": {
      "addTitle": "Add {{name}} to plan",
      "editTitle": "Edit {{name}}",
      "itemFallback": "item",
      "quantityLabel": "Plant quantity",
      "notesLabel": "Notes (optional)",
      "notesPlaceholder": "Specific requirements, variety notes...",
      "datesSection": "Planned dates (optional)",
      "sowLabel": "Sow",
      "transplantLabel": "Transplant",
      "harvestLabel": "Harvest",
      "cancel": "Cancel",
      "save": "Save item"
    },
    "slotModal": {
      "title": "Which plant do you want to grow in this slot?",
      "quickSelect": "Quick select from plan items:",
      "remaining": "{{remaining}} to place ({{placed}}/{{total}} placed)",
      "full": "Fully placed ({{placed}}/{{total}})",
      "or": "OR",
      "emptyPlan": "The plan is empty. Select the first plant.",
      "addFromPlantOpedia": "Add new plant from Plant-o-Pedia"
    },
    "plantSelector": {
      "title": "Select plant for plan"
    },
    "executeModal": {
      "title": "Execute plan",
      "toExecute": "To execute",
      "alreadyExecuted": "Already executed",
      "partialModeTitle": "Partial execution",
      "partialModeLabel": "Select items to execute (partial execution)",
      "selectedCount": "Selected items: {{count}} / {{total}}",
      "selectAll": "Select all",
      "deselectAll": "Deselect all",
      "noItems": "No items to execute",
      "slotId": "Slot: {{id}}",
      "planned": "Planned",
      "unknownSpecies": "Item",
      "forceTitle": "Force execution",
      "forceDescription": "If target slots are occupied, existing plants will be terminated.",
      "executeAll": "Execute all",
      "executePartial": "Execute ({{count}})"
    },
    "notFound": {
      "error": "Plan not found",
      "backToList": "Back to list"
    }
  },
  "deviceManagement": {
    "title": "Device management",
    "subtitle": "Manage connected devices and their configuration",
    "addDevice": "Add device",
    "header": {
      "title": "Fleet management of connected devices and OTA operations",
      "subtitle": "Overview includes device fleet status, restart and OTA workflow, search and basic metadata of individual units."
    },
    "badges": {
      "total": "{{count}} devices",
      "online": "{{count}} online",
      "errors": "{{count}} errors",
      "filter": "Filter: {{term}}",
      "noFilter": "No filter"
    },
    "stats": {
      "total": "Total devices",
      "online": "Online",
      "offline": "Offline",
      "errors": "Errors"
    },
    "search": "Search devices...",
    "table": {
      "name": "Name",
      "deviceId": "Device ID",
      "type": "Type",
      "status": "Status",
      "uptime": "Uptime",
      "lastSeen": "Last seen",
      "location": "Location",
      "actions": "Actions"
    },
    "status": {
      "online": "Online",
      "offline": "Offline",
      "error": "Error"
    },
    "firmware": {
      "unknown": "Unknown",
      "unknownVersion": "Unknown"
    },
    "lastSeenNever": "Never",
    "actions": {
      "restart": "Restart",
      "ota": "OTA Update",
      "edit": "Edit",
      "delete": "Delete"
    },
    "empty": {
      "title": "No devices",
      "description": "No devices have been registered yet."
    },
    "createModal": {
      "title": "Add new device",
      "nameLabel": "Device name",
      "deviceIdLabel": "Device ID",
      "deviceIdHint": "Unique device identifier",
      "typeLabel": "Device type",
      "locationLabel": "Location",
      "cancel": "Cancel",
      "create": "Create"
    },
    "editModal": {
      "title": "Edit device",
      "cancel": "Cancel",
      "save": "Save"
    },
    "otaModal": {
      "title": "OTA Update — {{deviceName}}",
      "currentFirmware": "Current firmware version:",
      "targetVersionLabel": "Target version",
      "selectVersion": "Select version",
      "versionStable": " (stable)",
      "versionBeta": " (beta)",
      "warning": "The device will be updated to version {{version}}. During the update the device will be temporarily unavailable.",
      "cancel": "Cancel",
      "start": "Start update"
    },
    "deleteConfirm": {
      "title": "Delete device",
      "confirmText": "Delete",
      "body": "Are you sure you want to delete device {{name}}? This action is irreversible."
    },
    "restartConfirm": {
      "title": "Restart device",
      "confirmText": "Restart",
      "body": "Are you sure you want to restart device {{name}}?"
    },
    "errors": {
      "createFailed": "Failed to create device",
      "updateFailed": "Failed to update device",
      "deleteFailed": "Failed to delete device",
      "restartFailed": "Failed to send restart command",
      "otaFailed": "Failed to start OTA update"
    },
    "success": {
      "restartSent": "Restart command sent",
      "otaStarted": "OTA update started"
    }
  },
  "growingAreaDetail": {
    "activeBatches": {
      "title": "Active plantings",
      "editLayoutLabel": "Edit planting of batch {{name}}",
      "unknownBatch": "Unknown batch",
      "unknownSpecies": "Unknown species"
    },
    "recommendations": {
      "empty": "No recommendations available",
      "emptyHint": "Try adding plants to the area.",
      "scoreTitle": "Overall suitability score: {{score}}%",
      "difficulty": "difficulty",
      "speciesDetail": "Species detail →",
      "plan": "Plan",
      "sow": "Sow",
      "allPlans": "All crop plans"
    },
    "bulkSow": {
      "title": "Bulk sowing",
      "close": "Close",
      "sowingInto": "Sowing into {{count}} selected slots.",
      "batchLabel": "Batch",
      "changeBatch": "Click to change batch",
      "selectBatch": "Select batch...",
      "seedsPerSlot": "Seeds per slot",
      "seedsHint": "Recommended: 15-20 (basil)",
      "cancel": "Cancel",
      "sow": "Sow {{count}} slots"
    },
    "areaTypes": {
      "tower": "Hydroponic tower",
      "bed": "Bed",
      "propagation": "Propagation tray / Seeder",
      "rack": "Rack system",
      "other": "Growing area"
    },
    "actions": {
      "settings": "Settings",
      "cropPlan": "Crop plan",
      "backToAreas": "Back to areas"
    },
    "stats": {
      "title": "Occupancy, moves and operational context",
      "subtitle": "Area detail combines slot occupancy, recommendations, linked batches and actions on individual positions.",
      "areaType": "Area type",
      "totalSlots": "Total slots",
      "occupied": "Occupied",
      "occupancy": "Occupancy",
      "percentOccupied": "{{pct}}% occupied",
      "moving": "Move active",
      "noMoving": "No move"
    },
    "grid": {
      "title": "Slot map",
      "selectionPoint": "Point",
      "selectionRange": "Range",
      "cancel": "Cancel",
      "bulk": "Bulk",
      "selectedSlots": "{{count}} slots selected",
      "bulkSow": "Bulk sow",
      "noSlots": "No slots in this area",
      "bulkPhase": "Bulk phase change: {{count}} plants",
      "changePhase": "Change phase"
    },
    "phases": {
      "seed": "Seed",
      "germinated": "Germinated",
      "true_leaves": "True leaves",
      "vegetative": "Vegetative",
      "flowering": "Flowering",
      "fruiting": "Fruiting",
      "harvested": "Harvested",
      "dead": "Dead",
      "empty": "Empty"
    },
    "aiPanel": {
      "title": "AI Recommendations"
    },
    "notFound": {
      "title": "Area not found",
      "description": "This growing area does not exist or has been deleted.",
      "backToList": "Back to list"
    },
    "removePlant": {
      "title": "Remove plant?",
      "description": "Are you sure you want to remove plant \"{{name}}\" from this slot?",
      "confirmText": "Remove",
      "cancelText": "Cancel"
    },
    "movePlant": {
      "title": "Move plant?",
      "description": "You will move plant \"{{name}}\" from slot {{from}}{{fromArea}} to slot [{{row}}, {{col}}] in area {{areaName}}.",
      "fromArea": " in area {{name}}",
      "confirmText": "Move",
      "cancelText": "Cancel"
    },
    "moveBanner": {
      "moving": "Moving plant",
      "source": "Source: {{slot}}",
      "crossArea": "You are selecting a target in area {{areaName}}.",
      "sameArea": "Select an empty slot in this or another area.",
      "changeArea": "Change area",
      "cancel": "Cancel"
    },
    "quickSow": {
      "title": "Direct planting",
      "description": "Creates a new batch for {{name}} and sows into the selected number of empty slots.",
      "slotsLabel": "Number of slots",
      "seedsLabel": "Seeds per slot",
      "available": "available: {{count}}",
      "total": "total: {{count}} seeds",
      "cancel": "Cancel",
      "sow": "Sow into {{count}} slots"
    },
    "toasts": {
      "bulkSowed": "Successfully sown into {{count}} slots",
      "bulkSowFailed": "Failed to bulk sow",
      "createBatchFailed": "Failed to create batch",
      "noEmptySlots": "No empty slots",
      "quickSowed": "{{name}}: sown into {{count}} slots",
      "quickSowFailed": "Failed to direct sow",
      "plantAssigned": "Plant assigned",
      "plantAssignFailed": "Failed to assign plant",
      "plantRemoved": "Plant removed",
      "plantRemoveFailed": "Failed to remove plant",
      "plantMoved": "Plant moved to slot {{slot}} in area {{area}}",
      "plantMoveFailed": "Failed to move plant",
      "eventRecorded": "{{label}} recorded",
      "eventFailed": "Failed to save event",
      "incidentReported": "Incident reported",
      "bulkPhaseFailed": "Failed to bulk change phase",
      "bulkPhaseChanged": "Successfully changed {{count}} plants",
      "slotOccupied": "Slot {{slot}} is occupied ({{occupiedBy}}). Select an empty slot.",
      "slotIsSource": "Slot {{slot}} is the source position. Select a different empty target.",
      "unknownArea": "This area could not be found."
    }
  },
  "userManagement": {
    "title": "User Management",
    "subtitle": "Manage user accounts and their permissions",
    "actions": {
      "add": "Add user"
    },
    "overview": {
      "title": "Account, role and permission management",
      "subtitle": "This page combines user search, roles and CRUD operations for accounts in one admin table.",
      "userCount": "{{count}} accounts",
      "adminCount": "{{count}} admin",
      "operatorCount": "{{count}} operators",
      "activeFilter": "Filter: {{term}}",
      "noFilter": "No filter"
    },
    "search": "Search users...",
    "table": {
      "created": "Created",
      "lastLogin": "Last login",
      "actions": "Actions",
      "editTitle": "Edit",
      "deleteTitle": "Delete",
      "never": "Never"
    },
    "empty": {
      "title": "No users",
      "description": "No users found yet."
    },
    "roles": {
      "admin": "Administrator",
      "operator": "Operator",
      "user": "User",
      "viewer": "Viewer",
      "device": "Device"
    },
    "createModal": {
      "title": "Create new user",
      "passwordLabel": "Password",
      "cancel": "Cancel",
      "create": "Create"
    },
    "editModal": {
      "title": "Edit user",
      "cancel": "Cancel",
      "save": "Save"
    },
    "deleteConfirm": {
      "title": "Delete user?",
      "confirmText": "Delete",
      "description": "Are you sure you want to delete this user? This action is irreversible."
    },
    "errors": {
      "createFailed": "Failed to create user",
      "updateFailed": "Failed to update user",
      "deleteFailed": "Failed to delete user"
    }
  },
  "growingAreaList": {
    "title": "My Growing Areas",
    "subtitle": "Manage your gardens and growing areas",
    "filterLabel": "Filter by device",
    "allDevices": "All devices",
    "gardenTypes": {
      "indoor": "Indoor",
      "greenhouse": "Greenhouse",
      "outdoor": "Outdoor"
    },
    "actions": {
      "newGarden": "New garden",
      "newArea": "New area"
    },
    "quickActions": {
      "cropPlans": "Crop plans",
      "cropPlansDesc": "Manage plans",
      "recommendations": "Recommendations",
      "recommendationsDesc": "What to grow",
      "recommendationsHint": "Select a device in the filter above"
    },
    "overview": {
      "title": "Area overview",
      "activeView": "Active view: {{device}}",
      "filtered": "Filtered",
      "all": "All",
      "gardensCount": "{{count}} gardens",
      "areasCount": "{{count}} areas",
      "devicesCount": "{{count}} devices"
    },
    "empty": {
      "title": "No areas or gardens",
      "description": "Start by creating a garden (e.g. Greenhouse, Apartment) or add a standalone area.",
      "createGarden": "Create garden",
      "addArea": "Add standalone area"
    },
    "gardenModal": {
      "createTitle": "Create garden",
      "editTitle": "Edit garden",
      "createDescription": "A garden helps group areas by location or growing method.",
      "editDescription": "Update the name and type so the overview reflects your actual layout.",
      "nameLabel": "Garden name",
      "namePlaceholder": "E.g. Greenhouse, Balcony or Top shelf",
      "typeLabel": "Garden type",
      "cancel": "Cancel",
      "create": "Create garden",
      "save": "Save changes",
      "nameRequired": "Please enter a garden name.",
      "createFailed": "Failed to create the garden.",
      "saveFailed": "Failed to save the garden."
    },
    "deleteArea": {
      "title": "Delete area?",
      "description": "Are you sure you want to delete this area? This action cannot be undone.",
      "confirm": "Delete",
      "cancel": "Cancel"
    },
    "deleteGarden": {
      "title": "Delete garden?",
      "description": "Are you sure you want to delete the garden \"{{name}}\"? Areas inside will be unassigned.",
      "confirm": "Delete garden",
      "cancel": "Cancel"
    }
  },
  "branchDetail": {
    "back": "Back",
    "close": "Close",
    "notFound": "Branch not found.",
    "noDescription": "No description",
    "targets": {
      "title": "Target conditions",
      "subtitle": "Based on active recipes",
      "temperature": "Temperature",
      "humidity": "Humidity",
      "light": "Light"
    },
    "activeBatches": {
      "title": "Active plants / batches",
      "addBatch": "Add another batch",
      "migrationNote": "Planting and transplanting is managed in the growing area detail.",
      "canonicalArea": "The canonical view for this legacy branch is now {{name}}.",
      "noCanonicalArea": "The canonical view is now in the Growing Areas section, not in the legacy branch detail.",
      "openArea": "Open area detail",
      "goToAreas": "Go to growing areas",
      "plantCount": "{{count}} plants",
      "day": "Day {{day}}",
      "detail": "Detail",
      "batchActions": "More batch actions",
      "editDate": "Edit date",
      "move": "Move",
      "removeBatch": "Remove from branch",
      "empty": "Nothing is growing on this branch.",
      "createBatch": "Create new batch"
    },
    "history": {
      "title": "Batch history",
      "empty": "No history."
    },
    "controls": {
      "title": "Branch controls",
      "lighting": "Lighting",
      "valves": "Valves"
    },
    "irrigation": {
      "title": "Irrigation status",
      "solenoid": "Solenoid",
      "off": "Off"
    },
    "collision": {
      "title": "Collision at position #{{slot}}",
      "description": "Multiple plants occupy this position. Select the one you want to edit (e.g. transplant):",
      "plantInfo": "Status: {{status}}, {{substrate}}",
      "unknownSubstrate": "Unknown"
    },
    "editDate": {
      "title": "Batch start date",
      "hint": "Changing the date will recalculate plant age.",
      "cancel": "Cancel",
      "save": "Save"
    },
    "removeBatch": {
      "title": "Remove batch from branch?",
      "description": "Are you sure you want to remove batch \"{{name}}\" from this branch? The batch will remain active but will not be assigned to any branch.",
      "confirm": "Remove",
      "cancel": "Cancel"
    },
    "deletePlant": {
      "title": "Delete plant?",
      "description": "Are you sure you want to permanently delete this plant?",
      "confirm": "Delete",
      "cancel": "Cancel"
    },
    "rack": {
      "title": "Rack view",
      "occupancy": "{{used}} / {{total}} used",
      "inactiveSlot": "Inactive slot",
      "slotOccupied": "Position {{slot}}",
      "slotFree": "Position {{slot}} (Free)",
      "healthy": "Healthy",
      "sick": "Sick",
      "dead": "Dead"
    },
    "toasts": {
      "batchRemoved": "Batch removed from branch",
      "batchRemoveFailed": "Failed to remove batch",
      "dateUpdated": "Start date updated",
      "dateUpdateFailed": "Failed to update date",
      "plantDeleted": "Plant deleted",
      "plantDeleteFailed": "Failed to delete plant"
    }
  },
  "safetyRules": {
    "toasts": {
      "deleted": "Rule deleted",
      "deleteError": "Error deleting rule",
      "toggled": "Rule status changed"
    },
    "modal": {
      "templateTitle": "New rule — choose template",
      "wizardTitle": "New rule setup",
      "editTitle": "Edit rule"
    },
    "deleteConfirm": {
      "title": "Delete rule?",
      "description": "Are you sure you want to delete this rule? This action is irreversible.",
      "confirm": "Delete",
      "cancel": "Cancel"
    },
    "empty": {
      "title": "No rules",
      "description": "Create your first rule for automatic system and plant protection."
    },
    "actions": {
      "new": "New rule",
      "selectAll": "Select all",
      "deselectAll": "Deselect all",
      "edit": "Edit rule",
      "delete": "Delete rule",
      "toggle": "Toggle rule {{name}}"
    },
    "status": {
      "active": "Active",
      "disabled": "Disabled"
    },
    "noDescription": "No description",
    "card": {
      "condition": "Condition",
      "action": "Action",
      "triggered": "Triggered",
      "lastTriggered": "Last triggered"
    },
    "conditionTypes": {
      "greater_than": "Greater than",
      "less_than": "Less than",
      "between": "Between",
      "duration_exceeded": "Duration exceeded",
      "state_mismatch": "State mismatch",
      "anomaly_detection": "Anomaly detection",
      "predictive": "Predictive"
    },
    "actionTypes": {
      "notification": "Notification",
      "send_alert": "Notification",
      "emergency_stop": "EMERGENCY STOP",
      "stop_pump": "Stop pump",
      "actuator_off": "Turn off device",
      "actuator_on": "Turn on device",
      "actuator_timed": "Device timer",
      "adjust_setting": "Adjust setting"
    },
    "info": {
      "heading": "Protection rules and escalation",
      "description": "Rules are used for emergency responses, resource limits and safety interventions on the active device.",
      "badge1": "Safety interventions",
      "badge2": "Escalation & limits",
      "badge3": "Manual & template creation"
    }
  },
  "myGarden": {
    "title": "My Garden",
    "subtitle": "Growing areas, reservoirs, batches and journal — all in one view.",
    "manageAll": "Manage",
    "tabs": {
      "areas": "Areas",
      "reservoirs": "Reservoirs",
      "batches": "Batches",
      "journal": "Journal"
    },
    "areas": {
      "count": "areas",
      "slots": "slots",
      "addArea": "New area",
      "empty": {
        "title": "No growing areas yet",
        "description": "Add your first area to start tracking your space."
      }
    },
    "reservoirs": {
      "count": "reservoirs",
      "addReservoir": "New reservoir",
      "empty": {
        "title": "No reservoirs yet",
        "description": "Add reservoirs and pair sensors to them."
      }
    },
    "batches": {
      "count": "batches",
      "addBatch": "New batch",
      "col": {
        "name": "Name",
        "species": "Species",
        "plants": "Plants",
        "status": "Status",
        "started": "Started"
      },
      "empty": {
        "title": "No batches yet",
        "description": "Start a new batch to track your growing cycle."
      }
    },
    "journal": {
      "count": "entries",
      "addEntry": "Add entry",
      "open": "Open journal",
      "empty": {
        "title": "Journal is empty",
        "description": "Entries from measurements, doses and plant care will appear here."
      }
    }
  },
  "interlocks": {
    "monitor": {
      "title": "Interlocks",
      "subtitle": "Active hardware and backend protection overview for this device",
      "tabCatalog": "Protection catalog",
      "tabActive": "Active ({{count}})",
      "layerFirmware": "Firmware",
      "layerBackend": "Backend",
      "severityCritical": "Critical",
      "severityHigh": "High",
      "severityMedium": "Medium",
      "severityLow": "Low",
      "statusArmed": "Armed",
      "statusTriggered": "Triggered",
      "statusDisabled": "Disabled",
      "autoResolves": "Auto-resolves",
      "blocksActuators": "Blocks outputs: {{ids}}",
      "noActive": "No active interlocks",
      "noActiveDesc": "All protections are in standby.",
      "reason": "Reason",
      "since": "Active since",
      "lastFired": "Last fired",
      "overridable": "Can be overridden",
      "notOverridable": "Cannot be overridden (firmware)",
      "edit": {
        "editBtn": "Edit",
        "editTitle": "Edit interlock",
        "fieldEnabled": "Active",
        "fieldSeverity": "Severity",
        "fieldAutoResolves": "Auto-resolves",
        "fieldOverridable": "Admin-overridable",
        "fieldParameters": "Parameters",
        "firmwareNote": "Firmware interlock — editable: state and parameters only",
        "saveBtn": "Save",
        "saveSuccess": "Interlock updated",
        "saveError": "Failed to save. Please try again.",
        "noParams": "No configurable parameters"
      }
    },
    "log": {
      "title": "Interlock log",
      "subtitle": "Chronological history of all interlock fires and resolutions",
      "noEvents": "No events",
      "noEventsDesc": "No interlocks have fired yet.",
      "resolved": "Resolved",
      "active": "Active",
      "loadMore": "Load more"
    },
    "overrides": {
      "title": "Admin overrides",
      "subtitle": "Temporary bypass of safety protections (admins only)",
      "createBtn": "New override",
      "noOverrides": "No active overrides",
      "noOverridesDesc": "No admin overrides are currently active.",
      "expiresIn": "Expires in {{minutes}} min",
      "expired": "Expired",
      "revokeBtn": "Revoke",
      "revokeConfirm": "Are you sure you want to revoke this override early?",
      "createModal": {
        "title": "Create admin override",
        "keys": "Interlock keys (select)",
        "allKeys": "All interlocks (*)",
        "reason": "Reason (min. 10 characters)",
        "reasonPlaceholder": "Why are you bypassing protections? (testing, emergency...)",
        "duration": "Duration",
        "duration5": "5 minutes",
        "duration15": "15 minutes",
        "duration30": "30 minutes",
        "duration60": "60 minutes",
        "warning": "I am bypassing system safety restrictions.",
        "warningCheck": "I understand and confirm",
        "submit": "Activate override",
        "errorReason": "Reason must be at least 10 characters.",
        "errorKeys": "Select at least one interlock.",
        "errorConfirm": "You must confirm that you understand the impact.",
        "successCreated": "Override activated",
        "errorCreate": "Failed to create override. Please try again."
      },
      "revokeSuccess": "Override revoked",
      "revokeError": "Failed to revoke override. Please try again."
    },
    "keys": {
      "ph_mutex": "pH pumps — mutual exclusion",
      "nutri_mutex": "Nutrients — mutual exclusion",
      "ph_fert_mutex": "pH + nutrients — mutual exclusion",
      "tank_low_block": "Low tank level",
      "suction_branch_required": "Suction pump without open branch",
      "sensor_unreliable_ph": "pH sensor unreliable",
      "sensor_unreliable_ec": "EC sensor unreliable",
      "sensor_unreliable_temp": "Temperature sensor unreliable",
      "daily_dose_limit": "Daily dosing limit exceeded",
      "anomaly_emergency_stop": "Anomaly — emergency stop",
      "calibration_stale": "Sensor calibration expired",
      "ota_in_progress": "Firmware update in progress",
      "quiet_hours": "Quiet hours",
      "manual_override_active": "Manual override active",
      "device_offline": "Device offline",
      "sensor_stale_data": "Sensor not sending data",
      "temperature_out_of_range": "Temperature out of allowed range"
    }
  },
  "service": {
    "title": "Service console",
    "tabs": {
      "status": "Status",
      "control": "Control",
      "service": "Service"
    },
    "stopAll": "STOP all",
    "on": "On",
    "off": "Off",
    "intensity": "Intensity",
    "tapToCalibrate": "Tap to calibrate",
    "disconnected": "disconnected",
    "noSensors": "No sensors to show",
    "calibratePh": "Calibrate pH",
    "calibrateEc": "Calibrate EC",
    "testActuators": "Test actuators",
    "stopTest": "Stop test",
    "resetEmergency": "Reset emergency",
    "emergencyActive": "Emergency stop is active — actuators are off.",
    "liveHint": "Live values from the active device",
    "temperature": "Temperature",
    "tempSensors": "temperature sensors",
    "status": "Status",
    "levelSensors": "level sensors",
    "noLevelSensors": "No level sensors",
    "flowSensors": "flow sensors",
    "noFlowSensors": "No flow sensors",
    "liveSummary": "Service summary",
    "liveSummaryText": "Quick readout for pH, EC, temperature, and level/flow status.",
    "liveOn": "Active",
    "liveOff": "Off",
    "rowMode": "Row mode",
    "liveWindow": "5 s window",
    "levelOk": "Level OK",
    "levelNok": "Level NOK",
    "flowRunning": "Flow running",
    "flowStopped": "Flow stopped",
    "modeHintTitle": "Service mode",
    "modeHintBody": "The mode defines what is safe to run on site. Maintenance and calibration block pumps and plant branches; test mode allows manual diagnostics.",
    "blockedInMode": "Blocked",
    "modeDescriptionTitle": "What the selected mode does",
    "modeDescription": "The selected mode acts as a working profile for a specific service task. Switch it based on the activity so the UI clearly shows what is allowed.",
    "modeGuard": "Guard",
    "activeMode": "Active",
    "pwmTitle": "PWM settings",
    "pwmPercent": "Power",
    "pwmDuration": "Run time",
    "minutes": "min",
    "pwmImmediate": "Immediate",
    "pwmApply": "Run PWM",
    "cancel": "Cancel",
    "modes": {
      "calibration": "Calibration",
      "calibrationDesc": "Sensors and pumps in a guided flow.",
      "calibrationGuard": "Allows calibration steps and readers, blocks pumps and plant branches.",
      "maintenance": "Maintenance",
      "maintenanceDesc": "Solution change, flush, cleaning and service checks.",
      "maintenanceGuard": "Keeps the system in a safe service state without starting pumps or plant branches.",
      "test": "Actuator test",
      "testDesc": "Sequential tests, ramp-up and ramp-down.",
      "testGuard": "Allows manual diagnostics and test runs without automatic interventions.",
      "emergency": "Emergency stop",
      "emergencyDesc": "Stop all actuators from one place."
    },
    "calibrationWizard": {
      "startRinse": "Start probe rinse",
      "startSolutionCalibration": "Start calibration for solution {{label}}",
      "startQc": "Start verification measurement {{label}}",
      "continue": "Continue",
      "proceedToQc": "Continue to verification",
      "saveRecord": "Save record (not used)"
    },
    "pumpCal": {
      "title": "Pump calibration",
      "pickHint": "Select a dosing pump to calibrate.",
      "mlPerSec": "ml/s",
      "never": "not calibrated",
      "primeHint": "Prime the tube and place a measuring cup under the outlet.",
      "check_container": "Measuring cup is under the tube outlet",
      "check_tube": "Tube is filled (no air)",
      "primeRun": "Prime line (8 s)",
      "priming": "Priming…",
      "primedDone": "Line primed",
      "measureHint": "Run the pump for a set time, measure the volume and add a point (ideally 2–3 points).",
      "duration": "Run time",
      "measuredMl": "Measured volume (ml)",
      "addPoint": "Add point",
      "runDose": "Run dose",
      "running": "Running…",
      "average": "Average",
      "reviewHint": "Saves the average of {{count}} measurements as the pump flow rate (ml/s) and the points to history.",
      "previous": "Previous",
      "new": "New",
      "save": "Save calibration",
      "saving": "Saving…",
      "next": "Continue",
      "back": "Back",
      "runError": "Pump run failed.",
      "saveError": "Saving calibration failed."
    },
    "confirm": {
      "resetEmergencyTitle": "Clear emergency stop?",
      "resetEmergencyBody": "Control of all hardware will be re-enabled — pumps, valves, heater and ozonizer. Make sure it is safe to run them.",
      "resetEmergencyConfirm": "Clear emergency stop",
      "startTestTitle": "Start test mode?",
      "startTestBody": "The test switches actuators on one by one, including pumps and valves. Do not run it with the hydraulics open or while working on the electronics.",
      "startTestConfirm": "Start test"
    }
  },
  "safetyRuleForm": {
    "sections": {
      "condition": "Condition",
      "action": "Action",
      "behaviour": "Behaviour"
    },
    "fields": {
      "ruleName": "Rule name",
      "ruleNamePlaceholder": "e.g. Protect pump at low level",
      "description": "Description",
      "descriptionPlaceholder": "What the rule is for and when it should act",
      "category": "Category",
      "sensorType": "Sensor type",
      "conditionType": "Condition type",
      "threshold": "Threshold",
      "minimum": "Minimum",
      "maximum": "Maximum",
      "hysteresis": "Hysteresis",
      "expectedValue": "Expected value",
      "durationMin": "Duration (min)",
      "lookbackHours": "Lookback window (h)",
      "lookbackHoursShort": "Window (h)",
      "predictionHours": "Forecast (h)",
      "stdDeviations": "Standard deviations",
      "actionType": "Action type",
      "alertLevel": "Alert severity",
      "pumpsToStop": "Pumps to stop",
      "settingName": "Setting name",
      "settingValue": "Setting value",
      "cooldown": "Cooldown between triggers (s)",
      "maxTriggersPerHour": "Max triggers per hour",
      "priority": "Priority",
      "priorityHint": "Higher number = evaluated sooner",
      "activeFrom": "Active from",
      "activeTo": "Active to",
      "daysOfWeek": "Days of week",
      "oneTimeRule": "One-time rule",
      "oneTimeRuleDesc": "Disables itself after the first trigger"
    },
    "hints": {
      "thresholdGt": "Triggers when the value rises above the threshold.",
      "thresholdLt": "Triggers when the value drops below the threshold.",
      "between": "Triggers when the value leaves the range.",
      "hysteresis": "Prevents flapping around the threshold. The rule stays active until the value returns by this margin.",
      "durationExceeded": "Triggers only after the condition holds for the whole period.",
      "stateMismatch": "Checks that device state matches expectations — e.g. a branch is open while the pump runs.",
      "anomalyDetection": "Compares the value against the sensor's usual behaviour and reacts to outliers.",
      "stdDeviations": "How far the value must deviate to count as an anomaly. Lower = more sensitive.",
      "predictive": "Estimates the trend from recent readings and warns before the limit is crossed.",
      "predictionHours": "How far ahead the trend should be projected.",
      "lookbackHours": "How much history defines the sensor's usual behaviour.",
      "lookbackForRegression": "How much history is used to compute the trend.",
      "cooldown": "Minimum pause between two triggers. Prevents alert floods.",
      "rateLimiting": "Cap on triggers per hour — a safeguard against loops."
    },
    "placeholders": {
      "thresholdGt": "Upper limit",
      "thresholdLt": "Lower limit",
      "limitValue": "Threshold value",
      "lowerBound": "Lower bound",
      "upperBound": "Upper bound",
      "expectedValue": "Expected state",
      "minutes": "Minutes",
      "predictionLimit": "Forecast limit"
    },
    "alertLevels": {
      "auto": "Automatic by severity"
    },
    "errors": {
      "required": "Please fill in this field."
    },
    "toasts": {
      "created": "Rule created.",
      "updated": "Rule saved.",
      "createError": "The rule could not be created. Please try again.",
      "updateError": "Changes could not be saved. Please try again."
    }
  }
}
